import React, { useEffect, useState } from "react";
import { reportsApi } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import ReportView from "../components/reports/ReportView";
import ReportBuilderV2 from "../components/reports/ReportBuilderV2";
import { CreditsPanel, MemosPanel, RatesPanel } from "../components/reports/BillingPanels";
import FamiliesPanel from "../components/reports/FamiliesPanel";
import { Button } from "../components/ui/button";
import { Search, Bookmark, Clock, Trash2, Settings2, Plus, PenSquare } from "lucide-react";
import { toast } from "sonner";

const INTERACTIVE = {
  credits_report: CreditsPanel,
  custom_memos: MemosPanel,
  custom_usage_rates: RatesPanel,
  account_families: FamiliesPanel,
};

export default function Reports() {
  const [catalog, setCatalog] = useState(null);
  const [filterMeta, setFilterMeta] = useState(null);
  const [my, setMy] = useState({ saved: [], recent: [] });
  const [selected, setSelected] = useState("spend_analysis");
  const [provider, setProvider] = useState("all");
  const [months, setMonths] = useState("6");
  const [groupBy, setGroupBy] = useState("service");
  const [account, setAccount] = useState("all");
  const [service, setService] = useState("all");
  const [customerId, setCustomerId] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [customReports, setCustomReports] = useState([]);
  const [customData, setCustomData] = useState(null);
  const [v2Reports, setV2Reports] = useState([]);

  const loadV2 = () => reportsApi.v2Saved().then(setV2Reports).catch(console.error);

  useEffect(() => {
    reportsApi.catalog().then(setCatalog).catch(console.error);
    reportsApi.filters().then(setFilterMeta).catch(console.error);
    reportsApi.my().then(setMy).catch(console.error);
    reportsApi.customList().then(setCustomReports).catch(console.error);
    loadV2();
  }, []);

  const isBuilder = selected === "__builder";
  const customId = selected.startsWith("custom:") ? selected.slice(7) : null;
  const v2Id = selected.startsWith("v2:") ? selected.slice(3) : null;
  const v2Report = v2Id ? v2Reports.find((r) => r.id === v2Id) : null;
  const meta = catalog?.reports?.find((r) => r.id === selected);
  const isInteractive = !!meta?.interactive;
  const hasEstateFilters = (filterMeta?.estate_reports || []).includes(selected);
  const hasCustomerFilter = (filterMeta?.customer_reports || []).includes(selected);

  const runParams = () => ({
    provider, months, group_by: groupBy,
    ...(hasEstateFilters ? { account, service } : {}),
    ...(hasCustomerFilter ? { customer_id: customerId } : {}),
  });

  useEffect(() => {
    if (!customId) { setCustomData(null); return; }
    setCustomData(null);
    reportsApi.customRun(customId).then(setCustomData)
      .catch((e) => toast.error("Report failed", { description: e?.response?.data?.detail || e.message }));
  }, [customId]);

  useEffect(() => {
    if (!selected || isInteractive || isBuilder || customId) { setData(null); return; }
    setLoading(true);
    reportsApi.run(selected, runParams())
      .then((d) => { setData(d); reportsApi.my().then(setMy).catch(() => {}); })
      .catch((e) => toast.error("Report failed", { description: e?.response?.data?.detail || e.message }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [selected, provider, months, groupBy, account, service, customerId, isInteractive, isBuilder, customId]);

  const save = async () => {
    try {
      await reportsApi.save(selected, `${data.name} (${provider}, ${months}mo)`, { provider, months, group_by: groupBy });
      toast.success("Saved to My Reports");
      setMy(await reportsApi.my());
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
  };

  const openSaved = (s) => {
    setSelected(s.report_id);
    if (s.params?.provider) setProvider(s.params.provider);
    if (s.params?.months) setMonths(String(s.params.months));
    if (s.params?.group_by) setGroupBy(s.params.group_by);
  };

  if (!catalog) return <div className="px-6 lg:px-10 py-8 text-muted-foreground">Loading Report Center…</div>;

  const filtered = catalog.reports.filter((r) => !search || r.name.toLowerCase().includes(search.toLowerCase()));
  const Interactive = INTERACTIVE[selected];

  return (
    <div className="px-6 lg:px-10 py-8 flex gap-6" data-testid="reports-page">
      {/* Catalog rail */}
      <aside className="w-[270px] shrink-0 space-y-4">
        <Button onClick={() => setSelected("__builder")} data-testid="create-report-button"
          className={`w-full rounded-sm h-9 text-xs text-white ${isBuilder ? "ring-2 ring-offset-1 ring-[#00579e]" : ""} bg-[#00579e] hover:bg-[#004580]`}>
          <Plus className="w-4 h-4 mr-1.5" /> Create Report
        </Button>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reports…"
            data-testid="report-search" className="pl-9 rounded-sm h-9 bg-white" />
        </div>

        {(v2Reports.length > 0 || customReports.length > 0) && (
          <div className="cs-card p-3" data-testid="custom-reports-card">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Custom Reports</div>
            {v2Reports.map((c) => (
              <div key={c.id} className="flex items-center gap-1.5 py-1 group">
                <PenSquare className="w-3 h-3 text-[#00579e] shrink-0" />
                <button onClick={() => setSelected(`v2:${c.id}`)} data-testid={`v2-report-${c.id}`}
                  className={`text-[11px] text-left truncate flex-1 ${v2Id === c.id ? "font-semibold text-[#00579e]" : "hover:text-[#00579e]"}`}>
                  {c.name}
                </button>
                <button onClick={async () => {
                  await reportsApi.v2Delete(c.id);
                  if (v2Id === c.id) setSelected("spend_analysis");
                  loadV2();
                }} data-testid={`delete-v2-${c.id}`} className="opacity-0 group-hover:opacity-100">
                  <Trash2 className="w-3 h-3 text-[#b91c1c]" />
                </button>
              </div>
            ))}
            {customReports.map((c) => (
              <div key={c.id} className="flex items-center gap-1.5 py-1 group">
                <PenSquare className="w-3 h-3 text-[#6d28d9] shrink-0" />
                <button onClick={() => setSelected(`custom:${c.id}`)} data-testid={`custom-report-${c.id}`}
                  className={`text-[11px] text-left truncate flex-1 ${customId === c.id ? "font-semibold text-[#00579e]" : "hover:text-[#00579e]"}`}>
                  {c.name}
                </button>
                <button onClick={async () => {
                  await reportsApi.customDelete(c.id);
                  if (customId === c.id) setSelected("spend_analysis");
                  setCustomReports(await reportsApi.customList());
                }} data-testid={`delete-custom-${c.id}`} className="opacity-0 group-hover:opacity-100">
                  <Trash2 className="w-3 h-3 text-[#b91c1c]" />
                </button>
              </div>
            ))}
          </div>
        )}

        {(my.saved.length > 0 || my.recent.length > 0) && (
          <div className="cs-card p-3" data-testid="my-reports-card">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">My Reports</div>
            {my.saved.map((s) => (
              <div key={s.id} className="flex items-center gap-1.5 py-1 group">
                <Bookmark className="w-3 h-3 text-[#00579e] shrink-0" />
                <button onClick={() => openSaved(s)} data-testid={`saved-${s.id}`}
                  className="text-[11px] text-left hover:text-[#00579e] truncate flex-1">{s.name}</button>
                <button onClick={async () => { await reportsApi.deleteSaved(s.id); setMy(await reportsApi.my()); }}
                  data-testid={`delete-saved-${s.id}`} className="opacity-0 group-hover:opacity-100">
                  <Trash2 className="w-3 h-3 text-[#b91c1c]" />
                </button>
              </div>
            ))}
            {my.recent.slice(0, 5).map((v) => (
              <div key={v.id} className="flex items-center gap-1.5 py-1">
                <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                <button onClick={() => setSelected(v.report_id)} data-testid={`recent-${v.report_id}`}
                  className="text-[11px] text-left text-muted-foreground hover:text-[#00579e] truncate flex-1">{v.name}</button>
                <span className="text-[9px] cs-mono text-muted-foreground">×{v.count}</span>
              </div>
            ))}
          </div>
        )}

        {catalog.categories.map((cat) => {
          const reports = filtered.filter((r) => r.category === cat);
          if (!reports.length) return null;
          return (
            <div key={cat} className="cs-card p-3" data-testid={`report-cat-${cat.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5">{cat}</div>
              {reports.map((r) => (
                <button key={r.id} onClick={() => setSelected(r.id)} data-testid={`report-item-${r.id}`}
                  className={`w-full text-left text-[12px] px-2 py-1.5 rounded-sm flex items-center gap-1.5 ${selected === r.id ? "bg-[#00579e] text-white font-medium" : "hover:bg-[hsl(var(--cs-tint))]"}`}>
                  {r.interactive && <Settings2 className="w-3 h-3 shrink-0 opacity-70" />}
                  {r.name}
                </button>
              ))}
            </div>
          );
        })}
      </aside>

      {/* Report body */}
      <div className="flex-1 min-w-0">
        {isBuilder ? (
          <div className="space-y-4">
            <div>
              <h3 className="cs-heading text-base font-semibold">Create Report</h3>
              <p className="text-[11px] text-muted-foreground">
                Provider-aware FOCUS reporting — pick a cloud, map dimensions to measures, add derived metrics, choose a visualization, save & export.
              </p>
            </div>
            <ReportBuilderV2 onSaved={loadV2} />
          </div>
        ) : v2Id ? (
          <div className="space-y-4">
            <div>
              <h3 className="cs-heading text-base font-semibold">{v2Report?.name || "Custom report"}</h3>
              <p className="text-[11px] text-muted-foreground">Saved custom report — edit, re-run, save or export below.</p>
            </div>
            {v2Report && <ReportBuilderV2 key={v2Report.id} savedReport={v2Report} onSaved={loadV2} />}
          </div>
        ) : customId ? (
          customData
            ? <ReportView data={customData} exportUrl={reportsApi.customExportUrl(customId)} />
            : <div className="text-sm text-muted-foreground py-10" data-testid="custom-report-loading">Running custom report…</div>
        ) : Interactive ? (
          <div className="space-y-4">
            <div>
              <h3 className="cs-heading text-base font-semibold">{meta?.name}</h3>
              <p className="text-[11px] text-muted-foreground">{meta?.description}</p>
            </div>
            <Interactive />
          </div>
        ) : loading && !data ? (
          <div className="text-sm text-muted-foreground py-10" data-testid="report-loading">Running report…</div>
        ) : (
          <ReportView
            data={data}
            onSave={save}
            exportUrl={reportsApi.exportUrl(selected, runParams())}
            controls={
              <>
                {hasEstateFilters && filterMeta && (
                  <>
                    <Select value={account} onValueChange={setAccount}>
                      <SelectTrigger className="rounded-sm h-8 text-xs w-[190px] bg-white" data-testid="report-account-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All accounts</SelectItem>
                        {filterMeta.accounts.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={service} onValueChange={setService}>
                      <SelectTrigger className="rounded-sm h-8 text-xs w-[170px] bg-white" data-testid="report-service-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All services</SelectItem>
                        {filterMeta.services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </>
                )}
                {hasCustomerFilter && filterMeta && (
                  <Select value={customerId} onValueChange={setCustomerId}>
                    <SelectTrigger className="rounded-sm h-8 text-xs w-[180px] bg-white" data-testid="report-customer-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All customers</SelectItem>
                      {filterMeta.customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {data?.group_by_options && (
                  <Select value={groupBy} onValueChange={setGroupBy}>
                    <SelectTrigger className="rounded-sm h-8 text-xs w-[120px] bg-white" data-testid="report-groupby-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{data.group_by_options.map((g) => <SelectItem key={g} value={g}>by {g}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                <Select value={provider} onValueChange={setProvider}>
                  <SelectTrigger className="rounded-sm h-8 text-xs w-[110px] bg-white" data-testid="report-provider-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All clouds</SelectItem>
                    <SelectItem value="gcp">GCP</SelectItem>
                    <SelectItem value="aws">AWS</SelectItem>
                    <SelectItem value="azure">Azure</SelectItem>
                    <SelectItem value="oci">Oracle Cloud</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={months} onValueChange={setMonths}>
                  <SelectTrigger className="rounded-sm h-8 text-xs w-[90px] bg-white" data-testid="report-months-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["3", "6", "12"].map((m) => <SelectItem key={m} value={m}>{m} mo</SelectItem>)}</SelectContent>
                </Select>
              </>
            }
          />
        )}
      </div>
    </div>
  );
}
